/**
 * @fileoverview Workflow task item editor card (MOR-696, renamed MOR-716)
 *
 * One editable workflow task: title, description, status, priority, labels,
 * assignee / assigned-by, parent (for subtasks), blocked-by dependencies, and
 * relative due / planned offsets. Collapsed rows show a compact summary; the
 * card expands for full editing. Numeric offsets use string drafts so a field
 * can be fully cleared mid-edit. Editing is local; the parent editor autosaves.
 *
 * The row's subtask button nests a new task under this one directly, so the
 * "Subtask of" picker is only needed to re-parent an existing task.
 */

import { useEffect, useState, type CSSProperties } from "react";
import type { useSortable } from "@dnd-kit/sortable";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  AutomationAction,
  AutomationTrigger,
} from "@cvx/automations/automationRules/schema";
import type { AppointmentType, JobStatus } from "@cvx/jobs/schema";
import {
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  GripVertical,
  ListPlus,
  Plus,
  Repeat,
  Settings2,
  Trash2,
  Zap,
} from "lucide-react";
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Label,
  Textarea,
} from "@/ui/primitives";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { cn } from "@/utils/misc";
import { TASK_PRIORITIES, priorityMeta } from "@/calendar/task-priority";
import { AutomationRuleDialog } from "@/components/settings/automations/AutomationRuleDialog";
import { AutomationRuleSummary } from "@/components/settings/automations/AutomationRuleSummary";
import { MemberOrRoleSelect } from "./MemberOrRoleSelect";
import { offsetBadgeText } from "./offsetLabels";

export type OffsetUnit = "days" | "business_days" | "months" | "quarter_end";

export interface DraftRecurrence {
  frequency: "daily" | "weekly" | "monthly" | "yearly";
  interval: number;
  count: number | null;
}

export interface DraftItem {
  localKey: string;
  title: string;
  description: string;
  priority: number | null;
  statusKey: string | null;
  labelKeys: string[];
  assigneeUserId: Id<"users"> | null;
  assignorUserId: Id<"users"> | null;
  /** MOR-748 — assign to a workflow role, resolved per job at apply time.
   *  Mutually exclusive with the fixed-user fields above. */
  assigneeRoleId: Id<"workflowRoles"> | null;
  assignorRoleId: Id<"workflowRoles"> | null;
  parentLocalKey: string | null;
  blockedByKeys: string[];
  dueOffsetDays: number | null;
  dueOffsetUnit: OffsetUnit | null;
  plannedOffsetDays: number | null;
  plannedOffsetUnit: OffsetUnit | null;
  plannedDurationDays: number | null;
  recurrence: DraftRecurrence | null;
}

const NONE = "__none__";

const OFFSET_UNIT_OPTIONS: ReadonlyArray<{
  value: OffsetUnit;
  label: string;
}> = [
  { value: "days", label: "Days" },
  { value: "business_days", label: "Business days" },
  { value: "months", label: "Months" },
  { value: "quarter_end", label: "Days from quarter end" },
];

const RECURRENCE_FREQUENCY_OPTIONS: ReadonlyArray<{
  value: DraftRecurrence["frequency"];
  label: string;
}> = [
  { value: "daily", label: "Days" },
  { value: "weekly", label: "Weeks" },
  { value: "monthly", label: "Months" },
  { value: "yearly", label: "Years" },
];

/**
 * An automation rule that targets a task, as shown in the card's recap
 * (name + badge-based trigger/action summary, MOR-736). Carries the full
 * trigger/actions so the recap can render coloured badges like the workflow
 * automations section — not just a plain-text summary.
 */
export interface TaskAutomationRule {
  _id: Id<"automationRules">;
  templateId?: Id<"taskTemplates">;
  templateName?: string;
  name: string;
  description?: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  conditions?: {
    appointmentTypes?: AppointmentType[];
    jobStatuses?: JobStatus[];
  };
  actions: AutomationAction[];
}

/**
 * Drag bindings forwarded from the parent's `useSortable`. The card root takes
 * `setNodeRef` + `style`; only the grip handle gets the activator listeners so
 * dragging never starts from the inline inputs. `canDrag` is false while a
 * search/filter is active (reordering a filtered subset would be ambiguous).
 */
export type ItemSortable = Pick<
  ReturnType<typeof useSortable>,
  "attributes" | "listeners" | "setActivatorNodeRef" | "setNodeRef"
> & {
  style?: CSSProperties;
  isDragging?: boolean;
  canDrag: boolean;
};

/** Parse a numeric-text draft into a number or null (empty/invalid → null). */
function toNumberOrNull(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n) : null;
}

function NumberField({
  label,
  hint,
  value,
  onCommit,
}: {
  label: string;
  hint?: string;
  value: number | null;
  onCommit: (next: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  useEffect(() => {
    setDraft(value === null ? "" : String(value));
  }, [value]);
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="text"
        inputMode="numeric"
        value={draft}
        placeholder="—"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => onCommit(toNumberOrNull(draft))}
        className="h-9"
      />
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Hint copy for each offset unit (MOR-836). */
function offsetUnitHint(unit: OffsetUnit | null): string {
  switch (unit) {
    case "business_days":
      return "Business days after the anchor (skips weekends & public holidays)";
    case "months":
      return "Calendar months after the anchor date";
    case "quarter_end":
      return "Days after the end of the anchor's calendar quarter (0 = quarter end)";
    default:
      return "Days after the anchor date";
  }
}

/**
 * Number + unit pair for a relative offset (MOR-836). Amount commits on
 * blur (string draft so the field can be cleared mid-edit); the unit
 * commits immediately.
 */
function OffsetField({
  label,
  amount,
  unit,
  onCommit,
}: {
  label: string;
  amount: number | null;
  unit: OffsetUnit | null;
  onCommit: (amount: number | null, unit: OffsetUnit | null) => void;
}) {
  const [draft, setDraft] = useState(amount === null ? "" : String(amount));
  useEffect(() => {
    setDraft(amount === null ? "" : String(amount));
  }, [amount]);
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1.5">
        <Input
          type="text"
          inputMode="numeric"
          value={draft}
          placeholder="—"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onCommit(toNumberOrNull(draft), unit)}
          className="h-9 w-16 shrink-0"
        />
        <Select
          value={unit ?? "days"}
          onValueChange={(value) =>
            onCommit(
              toNumberOrNull(draft),
              value === "days" ? null : (value as OffsetUnit),
            )
          }
        >
          <SelectTrigger className="h-9 min-w-0 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OFFSET_UNIT_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {offsetUnitHint(unit)}
      </p>
    </div>
  );
}

export function WorkflowItemCard({
  orgId,
  item,
  index,
  depth,
  statuses,
  labels,
  otherItems,
  disabled,
  selected,
  onSelectedChange,
  onChange,
  onRemove,
  onAddSubtask,
  sortable,
  defaultExpanded,
  autoFocusTitle,
  automationRules,
  templateId,
  templateName,
  workflowTaskTitles,
}: {
  orgId: Id<"organizations">;
  item: DraftItem;
  index: number;
  /** Nesting level in the parent/child tree (roots = 0). Each level indents
   *  one step so subtasks of subtasks read as nested. Defaults to 1 for any
   *  subtask when the caller doesn't compute depths (e.g. the flow view's
   *  single-card edit dialog). */
  depth?: number;
  statuses: ReadonlyArray<{ key: string; name: string; color?: string }>;
  labels: ReadonlyArray<{ key: string; name: string }>;
  /** Other items in the workflow (for parent + blocked-by pickers). */
  otherItems: ReadonlyArray<{ localKey: string; title: string }>;
  disabled?: boolean;
  /** Whether this row is ticked for bulk editing. */
  selected?: boolean;
  /** Toggle bulk-edit selection; omit to hide the checkbox. */
  onSelectedChange?: (selected: boolean) => void;
  onChange: (patch: Partial<DraftItem>) => void;
  onRemove: () => void;
  /** Add a nested task under this one; omit to hide the subtask button. */
  onAddSubtask?: () => void;
  /** Drag-to-reorder bindings from the parent's DnD context (optional). */
  sortable?: ItemSortable;
  /** Open with the detail fields showing (used by the visual builder's
   *  click-to-edit dialog). Defaults to expanded only for untitled tasks. */
  defaultExpanded?: boolean;
  /** Focus the title on mount — set for a just-added task so it can be typed
   *  straight away (and so the browser scrolls the new row into view). */
  autoFocusTitle?: boolean;
  /** Workflow automation rules that target this task (MOR-736) — shown as
   *  a ⚡ chip on the row and a detail block in the expanded editor. */
  automationRules?: ReadonlyArray<TaskAutomationRule>;
  /** When provided, the expanded editor offers a quick "Add automation"
   *  button that opens the rule builder pre-scoped to this task (MOR-736). */
  templateId?: Id<"taskTemplates">;
  templateName?: string;
  /** All task titles in the workflow — passed to the rule builder's pickers. */
  workflowTaskTitles?: ReadonlyArray<string>;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? !item.title);
  const [automationDialogOpen, setAutomationDialogOpen] = useState(false);
  // When set, the rule builder opens in edit mode for this existing rule;
  // when null the builder opens in "add" mode scoped to this task.
  const [editingRule, setEditingRule] = useState<TaskAutomationRule | null>(
    null,
  );

  const openAddAutomation = () => {
    setEditingRule(null);
    setAutomationDialogOpen(true);
  };
  const openManageAutomation = (rule: TaskAutomationRule) => {
    setEditingRule(rule);
    setAutomationDialogOpen(true);
  };

  const isSubtask = item.parentLocalKey !== null;
  const indentLevel = depth ?? (isSubtask ? 1 : 0);
  // Narrowed alias so the recurrence editor can spread without assertions.
  const recurrence = item.recurrence;
  const prio = priorityMeta(item.priority);
  const PrioIcon = prio.icon;
  const statusName = statuses.find((s) => s.key === item.statusKey)?.name;

  const parentOptions = otherItems.filter((o) => o.localKey !== item.localKey);
  const labelOptions = labels.map((l) => ({ value: l.key, label: l.name }));
  const blockedOptions = parentOptions.map((o) => ({
    value: o.localKey,
    label: o.title || "(untitled task)",
  }));

  const canDrag = sortable?.canDrag ?? false;

  return (
    <div
      ref={sortable?.setNodeRef}
      style={{
        ...sortable?.style,
        // Inline style rather than a Tailwind class: the step count is
        // unbounded, and dynamic class names would be purged from the build.
        ...(indentLevel > 0 ? { marginLeft: `${indentLevel * 1.5}rem` } : {}),
      }}
      className={cn(
        "rounded-lg border border-border bg-background",
        sortable?.isDragging && "opacity-60 ring-1 ring-primary/40",
      )}
    >
      {/* Summary row */}
      <div className="flex items-center gap-2 px-3 py-2">
        <span
          ref={sortable?.setActivatorNodeRef}
          {...(canDrag ? sortable?.attributes : {})}
          {...(canDrag ? sortable?.listeners : {})}
          className={cn(
            "text-muted-foreground/40",
            canDrag
              ? "cursor-grab touch-none active:cursor-grabbing"
              : "cursor-default",
          )}
          title={canDrag ? "Drag to reorder" : undefined}
        >
          <GripVertical className="size-4" />
        </span>
        {onSelectedChange && (
          <Checkbox
            checked={selected ?? false}
            onCheckedChange={(c) => onSelectedChange(c === true)}
            disabled={disabled}
            aria-label="Select task for bulk editing"
          />
        )}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-muted-foreground hover:text-foreground"
          title={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? (
            <ChevronDown className="size-4" />
          ) : (
            <ChevronRight className="size-4" />
          )}
        </button>
        {isSubtask && (
          <CornerDownRight className="size-3.5 shrink-0 text-muted-foreground/60" />
        )}
        <Input
          value={item.title}
          placeholder={`Task ${index + 1} title…`}
          autoFocus={autoFocusTitle}
          disabled={disabled}
          onChange={(e) => onChange({ title: e.target.value })}
          className="h-8 flex-1 border-0 px-1 shadow-none focus-visible:ring-0"
        />
        {!expanded && (
          <div className="flex shrink-0 items-center gap-1.5">
            {item.priority ? (
              <Badge variant="outline" color={prio.color}>
                <PrioIcon className="mr-1 size-3" />
                {prio.label}
              </Badge>
            ) : null}
            {statusName && <Badge variant="secondary">{statusName}</Badge>}
            {(item.dueOffsetDays !== null ||
              item.dueOffsetUnit === "quarter_end") && (
              <Badge variant="outline">
                {offsetBadgeText(item.dueOffsetDays ?? 0, item.dueOffsetUnit)}
              </Badge>
            )}
            {item.recurrence && (
              <Badge variant="outline">
                <Repeat className="mr-1 size-3" />
                Recurring
              </Badge>
            )}
          </div>
        )}
        {automationRules && automationRules.length > 0 && (
          <span
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
            title={automationRules.map((r) => r.name).join(", ")}
          >
            <Zap className="size-3" />
            {automationRules.length}
          </span>
        )}
        {onAddSubtask && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            disabled={disabled}
            title="Add subtask"
            onClick={onAddSubtask}
          >
            <ListPlus className="size-3.5 text-muted-foreground" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0"
          disabled={disabled}
          title="Remove task"
          onClick={onRemove}
        >
          <Trash2 className="size-3.5 text-destructive" />
        </Button>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="space-y-4 border-t border-border px-3 py-3">
          <div className="space-y-1">
            <Label className="text-xs">Description</Label>
            <Textarea
              value={item.description}
              placeholder="Optional notes for this task…"
              disabled={disabled}
              onChange={(e) => onChange({ description: e.target.value })}
              className="min-h-[60px]"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select
                value={item.statusKey ?? NONE}
                onValueChange={(v) =>
                  onChange({ statusKey: v === NONE ? null : v })
                }
                disabled={disabled}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Default (To Do)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Default (To Do)</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Priority</Label>
              <Select
                value={String(item.priority ?? 0)}
                onValueChange={(v) =>
                  onChange({ priority: Number(v) === 0 ? null : Number(v) })
                }
                disabled={disabled}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_PRIORITIES.map((p) => (
                    <SelectItem key={p.value} value={String(p.value)}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Assignee</Label>
              <MemberOrRoleSelect
                orgId={orgId}
                value={{
                  userId: item.assigneeUserId,
                  roleId: item.assigneeRoleId,
                }}
                onChange={(v) =>
                  onChange({
                    assigneeUserId: v.userId,
                    assigneeRoleId: v.roleId,
                  })
                }
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Assigned by</Label>
              <MemberOrRoleSelect
                orgId={orgId}
                value={{
                  userId: item.assignorUserId,
                  roleId: item.assignorRoleId,
                }}
                onChange={(v) =>
                  onChange({
                    assignorUserId: v.userId,
                    assignorRoleId: v.roleId,
                  })
                }
                placeholder="Nobody"
              />
            </div>

            {labelOptions.length > 0 && (
              <div className="flex flex-col gap-2 sm:col-span-2">
                <Label className="text-xs">Labels</Label>
                <MultiSearchableSelect
                  value={item.labelKeys}
                  onValueChange={(v) => onChange({ labelKeys: v })}
                  options={labelOptions}
                  placeholder="No labels"
                  searchPlaceholder="Search labels…"
                />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Label className="text-xs">Subtask of</Label>
              <Select
                value={item.parentLocalKey ?? NONE}
                onValueChange={(v) =>
                  onChange({ parentLocalKey: v === NONE ? null : v })
                }
                disabled={disabled || parentOptions.length === 0}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Top-level task" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Top-level task</SelectItem>
                  {parentOptions.map((o) => (
                    <SelectItem key={o.localKey} value={o.localKey}>
                      {o.title || "(untitled task)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-xs">Blocked by</Label>
              <MultiSearchableSelect
                value={item.blockedByKeys}
                onValueChange={(v) => onChange({ blockedByKeys: v })}
                options={blockedOptions}
                placeholder="Nothing"
                searchPlaceholder="Search tasks…"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <OffsetField
              label="Due offset"
              amount={item.dueOffsetDays}
              unit={item.dueOffsetUnit}
              onCommit={(amount, unit) =>
                onChange({ dueOffsetDays: amount, dueOffsetUnit: unit })
              }
            />
            <OffsetField
              label="Planned start offset"
              amount={item.plannedOffsetDays}
              unit={item.plannedOffsetUnit}
              onCommit={(amount, unit) =>
                onChange({ plannedOffsetDays: amount, plannedOffsetUnit: unit })
              }
            />
            <NumberField
              label="Planned duration (days)"
              value={item.plannedDurationDays}
              onCommit={(n) => onChange({ plannedDurationDays: n })}
            />
          </div>

          {/* Recurrence (MOR-837): applying the workflow creates a repeating
              task starting at the computed due date instead of a one-off.
              Recurring tasks can't be subtasks or have dependencies. */}
          <div className="space-y-2 rounded-md border border-border px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-semibold">
                <Repeat className="size-3.5" />
                Repeat this task
              </p>
              <Checkbox
                checked={item.recurrence !== null}
                disabled={
                  disabled || isSubtask || item.blockedByKeys.length > 0
                }
                onCheckedChange={(c) =>
                  onChange({
                    recurrence:
                      c === true
                        ? { frequency: "monthly", interval: 3, count: null }
                        : null,
                  })
                }
                aria-label="Repeat this task"
              />
            </div>
            {(isSubtask || item.blockedByKeys.length > 0) && (
              <p className="text-[11px] text-muted-foreground">
                Recurring tasks can't be subtasks or have blockers.
              </p>
            )}
            {recurrence && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <NumberField
                  label="Every"
                  value={recurrence.interval}
                  onCommit={(n) =>
                    onChange({
                      recurrence: {
                        ...recurrence,
                        interval: n && n >= 1 ? n : 1,
                      },
                    })
                  }
                />
                <div className="space-y-1">
                  <Label className="text-xs">Unit</Label>
                  <Select
                    value={recurrence.frequency}
                    onValueChange={(value) =>
                      onChange({
                        recurrence: {
                          ...recurrence,
                          frequency: value as DraftRecurrence["frequency"],
                        },
                      })
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RECURRENCE_FREQUENCY_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <NumberField
                  label="Stop after (occurrences)"
                  hint="Leave empty to repeat indefinitely"
                  value={recurrence.count}
                  onCommit={(n) =>
                    onChange({
                      recurrence: {
                        ...recurrence,
                        count: n && n >= 1 ? n : null,
                      },
                    })
                  }
                />
              </div>
            )}
          </div>

          {/* Automations that target this task (MOR-736) — recap of what the
              workflow's rules do to it, plus a quick "add automation" that
              opens the rule builder pre-scoped to this task. */}
          {(templateId || (automationRules && automationRules.length > 0)) && (
            <div className="space-y-2 rounded-md border border-amber-200/70 bg-amber-50/50 px-3 py-2.5 dark:border-amber-900/40 dark:bg-amber-950/20">
              <div className="flex items-center justify-between gap-2">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 dark:text-amber-300">
                  <Zap className="size-3.5" />
                  Automations affecting this task
                </p>
                {templateId &&
                  !disabled &&
                  (automationRules && automationRules.length > 0 ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 gap-1 text-xs"
                      title="Manage this task's automation"
                      onClick={() => openManageAutomation(automationRules[0])}
                    >
                      <Settings2 className="size-3.5" /> Manage automation
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 shrink-0 gap-1 text-xs"
                      disabled={!item.title.trim()}
                      title={
                        item.title.trim()
                          ? "Add an automation for this task"
                          : "Give the task a title first"
                      }
                      onClick={openAddAutomation}
                    >
                      <Plus className="size-3.5" /> Add automation
                    </Button>
                  ))}
              </div>
              {automationRules && automationRules.length > 0 ? (
                <ul className="space-y-2.5">
                  {automationRules.map((rule) => (
                    <li key={rule._id} className="min-w-0">
                      {templateId && !disabled ? (
                        <button
                          type="button"
                          onClick={() => openManageAutomation(rule)}
                          title="Manage this automation"
                          className="text-left text-xs font-medium text-foreground underline-offset-2 hover:underline"
                        >
                          {rule.name}
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-foreground">
                          {rule.name}
                        </span>
                      )}
                      <AutomationRuleSummary
                        orgId={orgId}
                        trigger={rule.trigger}
                        actions={rule.actions}
                        className="mt-1"
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No automations target this task yet.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quick task-scoped rule builder — pre-fills the "When" trigger with
          this task so the user doesn't retype it (MOR-736). */}
      {templateId && automationDialogOpen && (
        <AutomationRuleDialog
          orgId={orgId}
          open={automationDialogOpen}
          onOpenChange={(open) => {
            setAutomationDialogOpen(open);
            if (!open) setEditingRule(null);
          }}
          initial={editingRule ?? undefined}
          defaultTaskTitleContains={item.title}
          templateId={templateId}
          templateName={templateName}
          workflowTaskTitles={
            workflowTaskTitles ? [...workflowTaskTitles] : undefined
          }
        />
      )}
    </div>
  );
}
